import { Player, Spare } from "../../types/models";

/**
 * A player as the scoring UI needs them: the jersey and name shown on a
 * player button, the combined label the older dropdown lists use, and the
 * fields roster ordering compares on.
 */
export interface PlayerOption {
  id: number;
  jerseyLabel: string;
  name: string;
  isSpare: boolean;
  displayText: string;
  jerseyNum: number;
  lastName: string;
}

/** A team's selectable players, grouped for the player picker. */
export interface TeamRoster {
  teamId: number;
  teamName: string;
  players: PlayerOption[];
}

export function toPlayerOption(
  id: number,
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  jersey: string | null | undefined,
  isSpare = false,
): PlayerOption {
  const first = firstName || "";
  const last = lastName || "Unknown";
  const jerseyLabel = jersey || "-";

  return {
    id,
    jerseyLabel,
    name: first ? `${last}, ${first}` : last,
    isSpare,
    displayText: `#${jerseyLabel} - ${last}, ${first}${
      isSpare ? " (Spare)" : ""
    }`,
    jerseyNum: parseInt(jerseyLabel) || 999,
    lastName: last.toLowerCase(),
  };
}

/** Sort by jersey number first, then by last name. */
export function comparePlayerOptions(a: PlayerOption, b: PlayerOption): number {
  if (a.jerseyNum !== b.jerseyNum) {
    return a.jerseyNum - b.jerseyNum;
  }
  return a.lastName.localeCompare(b.lastName);
}

export function formatPlayersForDropdown(players: Player[]): PlayerOption[] {
  return players
    .map((p) => toPlayerOption(p.id, p.first_name, p.last_name, p.jersey))
    .sort(comparePlayerOptions);
}

// Players who can be credited with a goal or penalty for a team in this game:
// the roster minus absent players, plus the spares who played for the team.
// Spare details come from the game's spare records, so a spare is always
// available regardless of how many players the league has.
export function getAvailablePlayers(
  teamPlayers: Player[],
  teamSpares: Spare[],
): PlayerOption[] {
  const absentPlayerIds = new Set(
    teamSpares.map((spare) => spare.replaced_player_id),
  );

  const rosterOptions = teamPlayers
    .filter((p) => !absentPlayerIds.has(p.id))
    .map((p) => toPlayerOption(p.id, p.first_name, p.last_name, p.jersey));

  const spareOptions = teamSpares.flatMap((spare) =>
    spare.spare_player_id
      ? [
          toPlayerOption(
            spare.spare_player_id,
            spare.spare_player_first_name,
            spare.spare_player_last_name,
            spare.spare_player_jersey,
            true,
          ),
        ]
      : [],
  );

  return [...rosterOptions, ...spareOptions].sort(comparePlayerOptions);
}

/**
 * Players who can be marked absent for a team in this game: the whole roster,
 * minus anyone already recorded absent. Nothing in the API stops a second
 * spare record for the same player, so the picker is what prevents it.
 * `keepPlayerId` keeps the player that a spare being edited already replaces.
 */
export function buildAbsentCandidateRoster(
  teamId: number,
  teamName: string,
  teamPlayers: Player[],
  spares: Spare[],
  keepPlayerId: number | null = null,
): TeamRoster {
  const alreadyAbsent = new Set(
    spares
      .filter((spare) => spare.team_id === teamId)
      .map((spare) => spare.replaced_player_id),
  );

  return {
    teamId,
    teamName,
    players: teamPlayers
      .filter(
        (player) => !alreadyAbsent.has(player.id) || player.id === keepPlayerId,
      )
      .map((p) => toPlayerOption(p.id, p.first_name, p.last_name, p.jersey))
      .sort(comparePlayerOptions),
  };
}

/** The players a team can be credited with in this game, ready for the picker. */
export function buildTeamRoster(
  teamId: number,
  teamName: string,
  teamPlayers: Player[],
  spares: Spare[],
): TeamRoster {
  return {
    teamId,
    teamName,
    players: getAvailablePlayers(
      teamPlayers,
      spares.filter((spare) => spare.team_id === teamId),
    ),
  };
}
